import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Ship, AlertTriangle, Mail } from 'lucide-react';
import { api } from '../lib/api';
import { RagChip, fmtDay } from '../lib/shipments';
import ShipmentMessages from '../components/ShipmentMessages';
import Modal from '../components/ui/Modal';

export default function ShipmentsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    // Only the latest request's response may update the list.
    const mine = ++seq.current;
    try {
      const [s, m] = await Promise.all([
        api.get(`/shipments?status=${status}`),
        api.get('/shipments/messages?status=pending').catch(() => ({ messages: [] })),
      ]);
      if (mine !== seq.current) return;
      setRows(s.shipments || []);
      setMessages(m.messages || []);
      setError('');
    } catch (e) { if (mine === seq.current) setError(e.message); } finally { if (mine === seq.current) setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  async function digest(kind) {
    setBusy(true);
    try { await api.post(`/shipments/digests/${kind}`, {}); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  if (loading) return <div className="p-4 text-[var(--text-secondary)]">Loading shipments…</div>;

  const critical = rows.reduce((n, r) => n + (r.alarms_open?.critical || 0), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><Ship size={20} /> Shipments</h1>
          <p className="text-sm text-[var(--text-secondary)]">
            {rows.length} {status === 'active' ? 'active' : 'shown'}{critical ? ` · ${critical} critical alarm${critical === 1 ? '' : 's'}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
            {[['active', 'Active'], ['all', 'All']].map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)}
                className={`px-3 py-1.5 text-xs font-medium ${status === k ? 'bg-primary text-white' : 'bg-white text-[var(--text-secondary)] dark:bg-slate-800'}`}>{l}</button>
            ))}
          </div>
          <button disabled={busy} onClick={() => digest('partners')} className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-medium disabled:opacity-50">
            <Mail size={13} /> Draft partners digest
          </button>
          <button disabled={busy} onClick={() => digest('concria')} className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-2 text-xs font-medium disabled:opacity-50">
            <Mail size={13} /> Draft Concria weekly
          </button>
          <button onClick={() => setShowNew(true)} className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white">
            <Plus size={14} /> New shipment
          </button>
        </div>
      </div>

      {error && <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      <div className="space-y-2">
        {rows.map((s) => (
          <Link key={s.id} to={`/shipments/${s.id}`}
            className="block rounded-xl border border-[var(--border)] bg-white p-4 hover:border-primary dark:bg-slate-900">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-[var(--text-primary)]">{s.project_name || s.customer_name || s.order_ref}</p>
                <p className="text-xs text-[var(--text-secondary)]">
                  {s.order_ref}{s.concria_order_no ? ` · Concria ${s.concria_order_no}` : ''}{s.customer_name && s.project_name ? ` · ${s.customer_name}` : ''}
                </p>
              </div>
              <RagChip schedule={s.schedule} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
              <Info label="Now" value={s.current_milestone_label || 'No update yet'} />
              <Info label="Need on site" value={fmtDay(s.need_by_date)} />
              <Info label="Projected" value={fmtDay(s.schedule?.projected_delivery)} />
              <Info label="Vessel / ETA" value={s.vessel ? `${s.vessel} · ${fmtDay(s.eta)}` : (s.eta ? fmtDay(s.eta) : 'not booked')} />
            </div>
            {(s.alarms_open?.critical > 0 || s.alarms_open?.warning > 0) && (
              <div className="mt-2 space-y-1">
                {(s.alarms || []).slice(0, 3).map((a) => (
                  <p key={a.type} className={`flex items-start gap-1.5 text-xs ${a.severity === 'critical' ? 'text-red-600' : 'text-amber-700 dark:text-amber-400'}`}>
                    <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {a.message}
                  </p>
                ))}
              </div>
            )}
          </Link>
        ))}
        {!rows.length && !error && <p className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-secondary)]">{status === 'active' ? 'No active shipments.' : 'No shipments yet.'}</p>}
      </div>

      <h2 className="mb-2 mt-8 text-sm font-semibold text-[var(--text-primary)]">Updates waiting for a partner ({messages.length})</h2>
      <ShipmentMessages messages={messages} onChanged={load} emptyText="Nothing waiting. Drafts appear here when a milestone is logged." />

      {showNew && <NewShipmentModal onClose={() => setShowNew(false)} onCreated={(s, warnings) => {
        setShowNew(false);
        // eslint-disable-next-line no-alert
        if (warnings?.length) window.alert(`Heads up: ${warnings.join(' ')}`);
        navigate(`/shipments/${s.id}`);
      }} />}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">{label}</p>
      <p className="truncate font-medium text-[var(--text-primary)]">{value}</p>
    </div>
  );
}

function NewShipmentModal({ onClose, onCreated }) {
  const [f, setF] = useState({ order_ref: '', customer_name: '', project_name: '', need_by_date: '', origin: '', concria_order_no: '', customer_po: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ''));
      const r = await api.post('/shipments', body);
      onCreated(r.shipment, r.warnings);
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  }

  return (
    <Modal onClose={onClose} title="New shipment" maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-3">
        <Field label="Order ref *" value={f.order_ref} onChange={set('order_ref')} placeholder="e.g. VR-2026-OFFICE" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer" value={f.customer_name} onChange={set('customer_name')} />
          <Field label="Project" value={f.project_name} onChange={set('project_name')} />
          <Field label="Need on site by (pour date)" type="date" value={f.need_by_date} onChange={set('need_by_date')} />
          <label className="text-xs font-medium text-[var(--text-secondary)]">Origin
            <select value={f.origin} onChange={set('origin')} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm dark:bg-slate-800">
              <option value="">Unknown</option><option>Finland</option><option>Portugal</option><option>Other</option>
            </select>
          </label>
          <Field label="Concria order #" value={f.concria_order_no} onChange={set('concria_order_no')} />
          <Field label="Customer PO" value={f.customer_po} onChange={set('customer_po')} />
        </div>
        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm">Cancel</button>
          <button disabled={busy || !f.order_ref.trim()} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Create</button>
        </div>
      </form>
    </Modal>
  );
}

export function Field({ label, ...props }) {
  return (
    <label className="block text-xs font-medium text-[var(--text-secondary)]">{label}
      <input {...props} className="mt-1 w-full rounded-lg border border-[var(--border)] bg-white px-2 py-2 text-sm text-[var(--text-primary)] dark:bg-slate-800" />
    </label>
  );
}
